// Generator route (logged-in app). Keep keys in sync with pl/appGenerator.ts.
// Professional product English. User-facing render currency = "gems".
// Dynamic data (track filenames, pack/collection names, counts) stays in the
// component — only static visible copy is translated here.
//
// Session 08 (2026-09-07): the LOCKED vocabulary on this screen — "Track",
// "Clip packs", "Text", "Opening hook", "Preview", "pack(s)". Technical addresses
// with the old word (`batchQueued.*`) stay: they are addresses, not copy.
export const appGenerator = {
  empty: {
    title: 'Upload a track first',
    body: 'The generator needs music. Upload your track and EDIMI will detect the beat and the loudest 30 seconds, and cut the clips to the rhythm.',
    upload: 'Upload track',
  },
  steps: {
    choose: 'Choose',
    // A row that already carries a choice does not ask for one again (session 21, decision 9).
    change: 'Change',
    add: 'Add',
    notSelected: 'Not selected',
    loading: 'Loading…',
    music: {
      tag: 'Track',
      editLyrics: 'Edit lyrics',
      recent: '{n} most recent of {total}',
      search: 'Search in Music…',
      all: 'All tracks ({total})',
      fewer: 'Fewer tracks',
      // A tile without a waveform: the peaks are not computed yet (202) or the track
      // has no audio (409). No stand-in bars — a state in words (G4-generator-M2).
      noWave: 'waveform not computed yet',
      noResults: 'No track matches "{query}".',
    },
    shots: {
      tag: 'Clip packs',
      all: 'All packs ({total})',
      desc: 'Ready-made EDIMI packs or your own clips from Clips. EDIMI will cut them to the beat.',
      empty: 'No clip packs',
      emptyCta: 'Add a pack under Clips',
      loadError: 'We could not load your clip packs.',
      // Family keyed on `total`.
      selected_one: '{n} of {total} pack selected',
      selected_few: '{n} of {total} packs selected',
      selected_many: '{n} of {total} packs selected',
      clear: 'Clear',
      state: {
        ready: 'ready',
        uploading: 'uploading',
        uploadingProgress: '{done} of {total}',
        unusable: 'no usable clips',
      },
      // The "upload clips" tile does not upload here — it leads to Clips (decision 08/3).
      // Two words: the pack tile is 110 px wide and the longer label lost its last word (PROD1 FR-8).
      upload: 'Upload clips',
      uploadHint: 'opens Clips',
      own: 'yours',
      clips: '{count} clips',
      clips_one: '{count} clip',
      clips_few: '{count} clips',
      clips_many: '{count} clips',
      // A pack card the backend has no frames for yet: the clips are there, the
      // thumbnails are still being made (session 21, decision 5).
      freshCut: 'freshly cut - frames on the way',
    },
    lyrics: {
      tag: 'Text',
      styleLabel: 'Style:',
    },
    hooks: {
      tag: 'Opening hook',
      optional: 'optional',
      desc: 'A short opening line for the edit. Choose one or several hook packs.',
      loadError: 'We could not load your hooks.',
      emptyPre: 'No hook packs.',
      create: 'Add hooks',
      none: 'No hook',
      // A sample from the chosen collection — two real hooks, so you know what can come up.
      sample: 'From the "{name}" pack - {n} of {total}',
      // Hook count from `item_count`: the step summary and the group heading for 2+ packs.
      count_one: '{count} hook',
      count_few: '{count} hooks',
      count_many: '{count} hooks',
      valuesHelp: 'This pack uses variables. The values apply once to the whole series.',
      artistLabel: 'Artist name',
      artistHelp: 'We insert it exactly where {artist} appears.',
      artistInvalid: 'Remove braces and unsupported characters.',
      timestampLabel: 'Moment in the track',
      timestampHelp: 'Enter it manually as M:SS, e.g. 0:12. It is never taken from the track fragment.',
      timestampInvalid: 'Enter the moment as M:SS, e.g. 0:12. Seconds must be between 00 and 59.',
      invalidValues: 'Correct the template variable values.',
    },
  },
  // ── Rule 44 (owner, 21.09): the settings of a multiple choice live AT the row
  // that needs them. The "!" and the quiet gear carry no name of their own, so the
  // screen-reader name comes from this block; "Constants" is the owner's own word
  // and the same group as the "Variables" window (rule 33).
  stepSettings: {
    open: 'Step settings',
    attention: 'Step settings, needs attention',
    done: 'Done',
    // The sum is DATA, not a sentence, and runs in whole percent (rule 39).
    sum: 'Sum: 100 %',
    constants: 'Constants',
    // The start is blocked while a constant is empty (G5): a pill by the counter and a small way.
    missing: 'hook constants missing',
    fill: 'Fill in the constants',
  },
  preview: {
    miniTitle: 'Muted preview',
    miniPlay: 'Play preview', miniPause: 'Pause preview',
    miniNote: 'Your track and current settings. See your changes here.',
    title: 'Preview',
    waitingTitle: 'Waiting for a track',
    waitingBody: 'Pick a track in step 1 and the preview will play.',
    loadingTitle: 'Loading the preview',
    nothingTitle: 'The edit would be a black screen',
    unavailableTitle: 'The preview did not answer',
    unavailableBody: 'The render works anyway.',
    waiting: 'Pick a track to load the preview',
    failed: 'The preview could not load',
    unavailable: 'The preview is unavailable in this build.',
    // D-022: ready-made packs are shown as still frames in the preview (the same
    // JPEG as in the picker), not video. Said out loud, because the difference is
    // visible — staying silent would let the user think the finished edit stands
    // still too.
    stockStills: 'When a clip has no video preview, we show its frame. The clip moves in the finished edit.',
    // Draws a NEW set of clips from the same packs. "Shuffle", not "refresh" —
    // refresh would suggest the same result again.
    rerollTitle: 'Shuffle',
    toolbarLabel: 'Preview tools',
    socialGuideLabel: 'Covered areas',
    socialGuideShow: 'Show on the preview',
  },
  manual: {
    open: 'Edit manually',
    title: 'Edit clips and hook',
    // The manual plan's scope as data: a pill beside the dialog title and a row in
    // the series confirmation. The full sentence stays for the reader and in `title`.
    scope: 'edit 1 of {n}',
    scopeFull: 'Manual changes apply to the first previewed edit. The other edits in the series are shuffled normally.',
    confirmRow: 'Manual changes',
    previewTitle: 'Edit preview',
    clipsTitle: 'Clips on the timeline',
    clipsCount: 'Clips: {count}',
    selectClip: 'Select clip {n}',
    selectedClip: 'Selected clip: {n}',
    rerollClip: 'Shuffle selected clip',
    onlyClip: 'There is no other clip to draw in this configuration.',
    hookTitle: 'Hook',
    noHook: 'This edit has no hook.',
    rerollHook: 'Shuffle hook only',
    resizeHook: 'Resize hook width from the right',
    resizeHookLeft: 'Resize hook width from the left',
    scaleHookLeft: 'Scale the hook from the left corner',
    scaleHookRight: 'Scale the hook from the right corner',
    centerHook: 'Center',
    moveHook: 'Move the hook',
    onlyHook: 'Choose a collection with at least two hooks.',
    error: 'That element could not be shuffled. Try again.',
    syncing: 'Updating the hook preview before rendering.',
    syncError: 'The hook preview did not confirm the new text. Try again before rendering.',
    retry: 'Retry preview',
    liveNote: 'Manual changes create exactly the first previewed edit. The remaining edits in the series are shuffled automatically.',
    done: 'Done',
  },
  campaign: {
    title: 'Schedule publishing after the render',
  },
  appearance: {
    bars: 'Bars', noBars: 'Off', barSize: 'Bar height', barColor: 'Bar color',
    hook: 'Hook appearance', textColor: 'Text color', font: 'Font', effect: 'Effect',
    backgroundColor: 'Background color', none: 'None', shadow: 'Shadow', box: 'Background',
    customColor: '{target}: custom color', quickColor: '{target}: {color}',
    square: '1:1',
  },
  // Choosing the fragment of the track (≤30 s — SPEC: an edit comes from one window).
  trim: {
    cardTitle: 'Fragment for the edit',
    rowLabel: 'Edit fragment',
    cardSub: 'Every edit is made from one fragment, at most 30 seconds.',
    edit: 'Change the fragment',
    loudest: '{len} s · loudest window',
    windowLen: '{len} s',
    title: 'Pick the fragment',
    // The window cap as a label beside the chips, computed from the window's `maxLen`.
    maxLen: 'max {n} s',
    start: 'Start',
    end: 'End',
    length: 'Length',
    keys: {
      handle: 'handle by 0.1 s',
      shift: '+ arrow by 1 s',
      tab: 'other handle',
    },
    jumpTo: 'Jump to',
    rise: 'Rise {n}',
    fromPeaks: 'from real peaks',
    play: 'Play the fragment',
    pause: 'Pause',
    playEnding: 'Audition the ending',
    pauseEnding: 'Pause ending',
    fullMode: 'Full fragment',
    endingMode: 'Ending loop',
    readyToPlay: 'Ready to audition',
    fades: 'Fragment fades',
    fadeIn: 'Fade in',
    fadeOut: 'Fade out',
    fadeHelp: '0-{max} s on each side',
    fadeSummary: 'in {in}s · out {out}s',
    audioLoading: 'Preparing the audition…',
    audioError: 'The track could not be prepared for auditioning.',
    retryAudio: 'Try again',
    playbackError: 'The fragment could not play. Try again.',
    loading: 'Loading the waveform…',
    // The waveform is a convenience, not a precondition: the lane state alone,
    // with no tail about what is still allowed.
    noWave: 'waveform not computed yet',
    save: 'Save fragment',
    cancel: 'Cancel',
  },
  batch: {
    packs: '{count} packs',
    packs_one: '{count} pack',
    packs_few: '{count} packs',
    packs_many: '{count} packs',
  },
  // ── D-021: words per line ──────────────────────────────────────────────────
  // The limit is PER STYLE (`app/lyrics/lyric_categories.py`: brat 4;
  // elegant/statement/classic 6; the rest 8; bold_pink and karaoke unlimited),
  // and when it is passed the engine BREAKS THE LINE — `shouldForceBreak` in
  // `lineHelpers.ts` starts a new line and truncates NOTHING. So the copy never
  // says "will be cut" or "will not fit": that would announce a loss of text the
  // engine does not cause, and the user would shorten their lyrics for no
  // reason. The counter counts atoms (words joined by `glue_after` count as
  // one), hence "about".
  lyricLimits: {
    hint: 'This style fits about {max} words per line.',
    willSplit_one: 'One line is longer than {max} words - EDIMI will split it across more lines.',
    willSplit_few: '{count} lines are longer than {max} words - EDIMI will split them across more lines.',
    willSplit_many: '{count} lines are longer than {max} words - EDIMI will split them across more lines.',
    noLimit: 'This style has no word limit per line.',
  },
  // The last key segment is the EXACT engine value
  // (`props_builder.py` VALID_CLIP_FIT_MODES / `BasicComposition/schema.ts:127`).
  clipFit: {
    label: 'How clips are framed',
    cover: 'Full screen',
    square_cover: 'Whole clip',
    square_contain: 'With a margin',
    desc: {
      cover: 'the clip fills the 9:16 frame',
      square_cover: 'all of it visible, bars at the sides',
      square_contain: 'smaller clip, bars top and bottom',
    },
  },
  // The engine supports `both` and `top`; no bars means letterboxSize: 0.
  // Generator height/color controls beside the preview are verified by MP4.
  letterbox: {
    label: 'Cinematic bars',
    both: 'Top and bottom',
    top: 'Top only',
    size: 'Bar height: {value}%',
  },
  advanced: {
    title: 'Advanced',
  },
  // 429: the in-flight render ceiling. NOTHING WAS CHARGED — the refusal happens
  // BEFORE the spend, so the sentence says so outright. Without it the screen
  // returns to the untruth we corrected in Refunds: the user reads "it failed"
  // and goes looking for gems that never left their wallet.
  busy: {
    title: 'Too many renders at once',
    body: 'We did not start this render and no gems were charged. Try again in a moment.',
    retry: 'Try again',
  },
  // A failure to LOAD the list, not a loss of material. The first clause says the
  // music is still there, because an empty screen after uploading tracks reads
  // like a wiped account.
  loadError: {
    title: 'We could not load your tracks',
    // P03 proposed "…could not reach it" — the forbidden-vocab lint bans
    // "reach" outright (invariant #4), so that wording would fail CI. Same
    // meaning without the word.
    body: 'Your music is still there.',
    retry: 'Try again',
  },
  cutMode: {
    label: 'Cut mode',
    hint: 'changes the preview at once',
    // The eight modes from `lib/cutModes.ts`. These values are the English the
    // screen has been rendering all along, through the `tx()` bridge in
    // Generator.tsx - so for an English reader nothing changes, and for a Polish
    // one the screen stops being in English.
    brak: 'No cuts',
    co_pol_bita: 'Every half beat',
    co_bit: 'Every beat',
    co_2_bity: 'Every 2 beats',
    co_4_bity: 'Every 4 beats',
    co_8_bitow: 'Every 8 beats',
    tylko_downbeaty: 'Downbeats only',
    custom: 'Custom',
  },
  // The "Custom cuts" window (package 40b, MAPA 39-R3 "New texts R3"): names and data only, no instruction sentences.
  customCuts: {
    open: 'Tap out',
    title: 'Custom cuts',
    unit_one: 'cut',
    unit_few: 'cuts',
    unit_many: 'cuts',
    play: 'Play',
    stop: 'Stop',
    undo: 'Undo',
    clear: 'Clear',
    done: 'Done',
    // The label on the drawn key (1440) and on the tap field (390).
    key: 'Space',
    tap: 'Tap',
    // Screen reader names.
    speed: 'Speed',
    add: 'Add cut',
    remove: 'Remove cut',
    rejected: 'cut rejected',
    audioError: "Couldn't load the audio.",
    // Confirmation when the fragment changes while tapped cuts exist: the effect is the title.
    changeFragment_one: 'Changing the fragment removes {count} cut',
    changeFragment_few: 'Changing the fragment removes {count} cuts',
    changeFragment_many: 'Changing the fragment removes {count} cuts',
    changeFragmentConfirm: 'Change fragment',
    changeTrack_one: 'Changing the track removes {count} cut',
    changeTrack_few: 'Changing the track removes {count} cuts',
    changeTrack_many: 'Changing the track removes {count} cuts',
    changeTrackConfirm: 'Change track',
  },
  presets: {
    templateLabel: 'Template',
    newTemplate: 'New template',
    loadTitle: 'Load template',
    saveTitle: 'Save template',
    saveOpen: 'Name and save template',
    label: 'Saved templates',
    save: 'Save',
    load: 'Load template',
    select: 'Choose a saved template',
    namePlaceholder: 'Template name',
    loading: 'Loading templates…',
    empty: 'You have no saved templates yet.',
    retry: 'Try again',
    loadError: 'We could not load your saved templates.',
    active: 'active: {name}',
    delete: 'Delete template "{name}"',
    saved: 'Template "{name}" saved.',
    saveError: 'The template could not be saved.',
    deleted: 'Template "{name}" deleted.',
    deleteError: 'The template could not be deleted.',
    saveHint: 'A template remembers the whole setup: the track with its fragment, packs, text, hooks and advanced settings.',
    loadHint: 'A template also loads its track and fragment when step 1 shows no other track.',
  },
  // Shares are NUMBERS OF EDITS (ruling 4b), not proportions or percentages:
  // the row says outright how many edits a style gets, and the sum matches the series.
  mix: {
    on: 'Set the style proportions. Edit counts match the percentages as closely as possible, with their order shuffled.',
    percentSum: 'Total: 100%',
    percentLabel: 'Percentage share: {name}',
    // Plural family keyed on `total`.
    result_one: '{n} of {total} edit',
    result_few: '{n} of {total} edits',
    result_many: '{n} of {total} edits',
    sum_one: 'total: {total} edit',
    sum_few: 'total: {total} edits',
    sum_many: 'total: {total} edits',
    stepper: 'Share: {name}',
    add: 'Add a style',
    remove: 'Remove {name} from the mix',
    tooFew: 'Fewer edits than rows - the shares are drawn at random. Add edits to set them.',
  },
  shares: {
    packs: 'Clip pack proportions', hooks: 'Hook pack proportions',
    mixedHelp: 'Percentages set how many clips come from each pack across the series, rounded to the closest possible clip counts.',
    singleHelp: 'Percentages set how many edits use each pack. One edit uses only one pack.',
    hookHelp: 'Percentages set how many edits use a hook from each pack, with counts as close to these proportions as possible.',
  },
  packMode: {
    shortLabel: 'Mode',
    mixedTooltip: 'Mixes clips from the selected packs within one edit.',
    singleTooltip: 'Each edit uses one pack. Clips from different packs stay in separate edits.',
    label: 'How to use the clip packs',
    mix: 'Mix them all',
    mixHelp: 'Every edit draws clips from all the selected packs.',
    single: 'One pack per edit',
    singleHelp: 'Each edit uses one pack, so it looks like a single shoot. The number next to a pack says how many edits get it.',
    mixShort: 'mix of all',
    singleShort: 'one pack per edit',
  },
  cost: {
    amount_one: '1 token',
    amount_few: '{count} tokens',
    amount_many: '{count} tokens',
  },
  // The sentence next to the DISABLED generate button (P03, R-1(ii)). Shown only
  // when the preview has ANSWERED `drawsNothing === true` — never on `undefined`,
  // because no answer is not an empty configuration, and a button disabled by a
  // slow preview would block a good edit for no reason.
  // Says WHAT to do (the three things the user controls) before why: it sits under
  // a dead button, so it has to be an instruction first and a diagnosis second.
  nothingToDraw: 'Add a clip pack, text or a hook - without one the edit would be a black screen.',
  // Paczka P1 C (reguła 48): SAM CZASOWNIK. Kwotę dokłada `PayLabel` jako daną
  // (moneta + liczba), więc etykieta nie nosi już ani liczby, ani słowa waluty.
  generateVerb: 'Generate',
  generatePlain: 'Generate edit',
  batchQueued: {
    // The toast carries the fact and the SIGNED amount; since paczka P1 C the amount is
    // a coin plus a number, so the old `cost_*` keys with the word are gone.
    title: 'Series queued',
  },
  design: {
    intro: 'Your track, chosen clips and text. Build your edit.',
    musicHint: 'Start with music and pick the best fragment.',
    packsHint: 'Set the mood with ready-made packs or your own clips.',
    textHint: 'Choose how your lyrics appear to the rhythm.',
    hookHint: 'An optional opening line. You choose when to add it.',
    mixHint: '{n} styles in your chosen proportions',
    cardTitle: 'Series',
    seriesName: 'Series name (optional)',
    countLabel: 'How many edits',
    countHint: 'Type a number or pick a shortcut · up to {max} in one series',
    allStyles: 'All 16 styles - {hidden} more',
    fewerStyles: 'Fewer styles - show {n}',
    noTextStyle: 'No text',
    styleMeta: 'text in the rhythm of the track',
    noTextMeta: 'frame and hook only',
    goToStep: 'To step {n}',
    goToClips: 'To Clips',
    // The preview state word — the pill above the frame (G4-generator-25), derived from the frame.
    previewWaiting: 'waiting for a track',
    previewLoading: 'loading',
    previewUnavailable: 'unavailable',
    previewPlaying: 'live',
    previewNothing: 'nothing to draw',
    restoreLoadingTitle: 'Restoring the series settings',
    restoreLoadingBody: 'Waiting for the saved form. The generator stays locked until this series is verified.',
    restoreErrorTitle: 'This series could not be restored',
    restoreErrorBody: 'The link is stale or the series does not belong to this account. We will not render with default settings.',
    restoreStartFresh: 'Set up a new series',
    styleLimit: 'Approximate per-line limit: {max}.',
    styleNoLimit: 'This style writes one word at a time. Nothing will be split.',
    // Three facts without repeats (G4-generator-05): edits × price = gems / balance before → after / time.
    costLine: '{edits} × {gems}',
    balanceLine: 'Balance {balance} · {after} left',
    remainingBalance: '{after} left',
    renderTimeLabel: 'Render time',
    renderTime: 'about 1-1.5 min per edit',
    stickyLine: '{edits} × {gems} · {after} left',
    reduce_one: 'Reduce to {count} edit',
    reduce_few: 'Reduce to {count} edits',
    reduce_many: 'Reduce to {count} edits',
    insufficient: 'You have {balance}, need {cost} - {missing} short.',
    topup: 'Top up tokens',
    ready: 'Everything is ready: track, clips and style.',
    waitingMusic: 'Pick a ready track first.',
    waitingPacks: 'Choose clips.',
    unusablePacks: 'The selected pack has no usable clips. Pick an EDIMI pack or approve clips under Clips.',
    waitingConfig: 'Waiting for the price configuration - just a moment.',
    restoringSeries: 'Loading the series settings…',
    // Not called since session 19 (decision 2: the backend guards the parallel render
    // limit, `too_many_inflight` → the busy dialog). Kept: nothing replaces it.
    seriesInProgress: 'A series is in progress - a new one once it finishes.',
    // Session 19 C: the header button takes the generator back to its starting state.
    // A saved template stays in the templates - it is unpinned, never deleted.
    clear: 'Clear',
    clearNone: 'Nothing to clear.',
    clearConfirmTitle: 'Clear the settings?',
    clearConfirmBody: 'The Generator will be empty - you pick the track, packs and the rest again. Your saved template “{name}” stays in your templates - we only unpin it.',
    clearItems: {
      track: 'Track and the chosen fragment',
      packs: 'Clip packs ({n}) with weights and mode',
      style: 'Text style and look',
      hooks: 'Hooks - opening hook, collection, weights',
      series: 'Series: number of edits (back to 1) and name',
      advanced: 'Advanced: cut mode, seed, variants, clip fit, manual plan',
      audio: 'Audio: trim and transitions',
      template: 'Template goes back to “New template”',
    },
  },
  styleGroups: {
    clean: 'Clean',
    loud: 'Loud',
    y2k: 'Y2K',
    soft: 'Soft',
    none: 'No captions',
    search: 'Search styles…',
    sheetTitle: 'All styles ({total})',
  },
  seriesCard: {
    eyebrow: 'Current series',
    untitled: 'Untitled series',
    renderingTitle: 'Series "{title}" is rendering',
    partialTitle: 'Series "{title}" needs attention',
    readyTitle: 'Series "{title}" is ready',
    failedTitle: 'Series "{title}" failed',
    emptyTitle: 'Series "{title}" is empty',
    renderingStatus: '{done} of {total} ready',
    partialStatus: '{done} of {total} ready',
    readyStatus: '{done} of {total} ready',
    failedStatus: '0 of {total} ready',
    emptyStatus: 'no edits',
    editName: 'Edit {n}',
    styleUnknown: 'style from saved settings',
    progressLabel: 'Progress of edit {n}',
    chunks: '{done} of {total} chunks',
    queued: 'queued',
    rendering: 'rendering',
    done: 'ready',
    failed: 'failed',
    refundConfirmed: 'Confirmed refund: {gems}',
    refundPending: 'The refund is still being reconciled',
    retry: 'Retry edit {n}',
    retryError: 'Could not retry this edit.',
    ledger: 'Charged from the account: {charged}. Confirmed refunds: {refunded}.',
    open: 'Open the series',
    newSeries: 'Set up another series',
  },
  invalid: {
    title: 'Invalid configuration',
    sub: 'Check your track and clip selection, then try again.',
    ok: 'Close',
    templateTitle: 'Fill in the hook constants',
    templateMissing: 'Complete: {fields}.',
    templateInvalid: 'Correct these fields: {fields}. Nothing was charged.',
    nickField: 'artist handle',
    titleField: 'title',
    fixTemplateValues: 'Go to the constants',
  },

  // ── Package 39 (minimal language, MINIMAL-2026-09-18) ────────────────────────
  origin: {
    series: 'from the "{name}" series',
    music: 'from Music',
  },
  pill: {
    waitingTrack: 'waiting for a track',
    analyzing: 'analysing',
    noLyrics: 'no lyrics',
    beatsUnknown: 'beat not measured',
    badFile: 'broken file',
    analysisFailed: 'analysis failed',
    noPacks: 'no clip packs',
    pickClips: 'pick clips',
    unusable: 'no usable clips',
    freshCut: 'freshly cut',
    black: 'black screen',
    counting: 'rendering',
    noAnswer: 'no answer',
    locked: 'account awaiting approval',
    noWave: 'waveform loading',
    missing_one: '{count} token short',
    missing_few: '{count} tokens short',
    missing_many: '{count} tokens short',
  },
  menu: {
    template: 'Template',
    load: 'Load template',
    save: 'Save template',
  },
  more: {
    open: 'More options',
    title: 'More options',
  },
  panel: {
    appearance: 'Bar and hook appearance',
    zones: 'Occlusion zones',
    cut: 'Cut rhythm',
  },
  card: {
    collapse: 'Collapse preview',
    expand: 'Expand preview',
  },
  sheet: {
    tracks: 'Tracks · {total}',
    packs: 'Clip packs · {total}',
  },
  pick: {
    fromMusic: 'Pick from Music',
    plain: 'Done',
    packs_one: 'Done · {count} pack',
    packs_few: 'Done · {count} packs',
    packs_many: 'Done · {count} packs',
    styles_one: 'Done · {count} style',
    styles_few: 'Done · {count} styles',
    styles_many: 'Done · {count} styles',
  },
  flow: {
    confirmTitle_one: 'Generate {count} edit?',
    confirmTitle_few: 'Generate {count} edits?',
    confirmTitle_many: 'Generate {count} edits?',
    cost: 'Cost',
    have: 'You have',
    after: 'Left',
    missing: 'Short by',
    noTokensTitle: 'Not enough tokens',
    progressTitle: 'Building your edit…',
    doneTitle: 'Edit ready',
    seeEdits: 'See edits',
    failedTitle: 'Render error',
    status: 'Status',
    statusFailed: 'Error',
    refunded: 'Tokens returned',
    retry_one: 'Try again · {count} token',
    retry_few: 'Try again · {count} tokens',
    retry_many: 'Try again · {count} tokens',
  },
}
